import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

import { disconnectSocket } from '@/lib/socket';
import { useAppStore } from '@/store/app.store';
import type { DecPagoOperacionDto, F36ResumenDto } from '@/types/api';

function getStore() {
  return useAppStore.getState();
}

export const api = axios.create({
  baseURL:         (import.meta.env.VITE_API_URL as string | undefined) ?? '/api/v1',
  headers:         { 'Content-Type': 'application/json' },
  timeout:         30_000,
  withCredentials: true,  // envía la cookie httpOnly refresh_token en /auth/refresh
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const { token, activeTenantId } = getStore();
  if (token)           config.headers.Authorization  = `Bearer ${token}`;
  if (activeTenantId)  config.headers['X-Tenant-Id']   = activeTenantId;
  return config;
});

// ── 401 → refresh token → retry ──────────────────────────────────────────────

type PendingResolve = (token: string) => void;

let isRefreshing  = false;
let pendingQueue: PendingResolve[] = [];
let proactiveTimer: ReturnType<typeof setTimeout> | null = null;

function flushQueue(token: string) {
  pendingQueue.forEach(cb => { cb(token); });
  pendingQueue = [];
}

export function forceLogout(reason?: string) {
  if (proactiveTimer) { clearTimeout(proactiveTimer); proactiveTimer = null; }
  getStore().logout();
  disconnectSocket();

  const { pathname } = window.location;
  const isAuthPage   = pathname.startsWith('/auth') || pathname === '/';

  const params = new URLSearchParams();
  if (reason)       params.set('reason',   reason);
  if (!isAuthPage)  params.set('redirect', pathname);

  const qs = params.toString();
  window.location.href = `/auth/login${qs ? `?${qs}` : ''}`;
}

export function jwtSv(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.sv === 'number' ? payload.sv : null;
  } catch { return null; }
}

/** Extrae el campo `exp` del payload JWT sin librerías externas. */
function jwtExp(token: string): number | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch { return null; }
}

/** Ejecuta el refresh. El login nativo usa la cookie httpOnly; las sesiones SSO
 * (vía ecocont-sso) llevan el refresh token en el store porque el gateway nunca
 * setea la cookie en el navegador. */
async function doRefresh(): Promise<string | null> {
  if (isRefreshing) return null;
  isRefreshing = true;
  try {
    const { refreshToken } = getStore();
    const res = await api.post<{ accessToken: string; expiresIn: number; refreshToken?: string }>(
      '/auth/refresh',
      refreshToken ? { refreshToken } : {},  // cookie refresh_token se envía automáticamente (withCredentials: true)
    );
    const { accessToken } = res.data;
    getStore().setAuth(accessToken, getStore().user!);
    if (res.data.refreshToken) getStore().setRefreshToken(res.data.refreshToken);
    flushQueue(accessToken);
    scheduleProactiveRefresh(accessToken);
    return accessToken;
  } catch (e: unknown) {
    pendingQueue = [];
    const errData = (e as { response?: { data?: { code?: string } } })?.response?.data;
    const reason  = errData?.code === 'SESSION_REVOKED' ? 'session_revoked' : undefined;
    forceLogout(reason);
    return null;
  } finally {
    isRefreshing = false;
  }
}

/**
 * Programa un refresh silencioso 2 minutos antes de que expire el access token.
 * Se llama al login y tras cada refresh exitoso.
 */
export function scheduleProactiveRefresh(token?: string) {
  if (proactiveTimer) { clearTimeout(proactiveTimer); proactiveTimer = null; }
  const t = token ?? getStore().token;
  if (!t) return;
  const exp = jwtExp(t);
  if (!exp) return;
  const msLeft = exp * 1000 - Date.now() - 2 * 60 * 1000; // 2 min antes de expirar
  if (msLeft <= 0) {
    void doRefresh();
    return;
  }
  proactiveTimer = setTimeout(() => { proactiveTimer = null; void doRefresh(); }, msLeft);
}

api.interceptors.response.use(
  res => res,
  async (err: AxiosError) => {
    const original = err.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // No intentar refresh en endpoints de auth — son públicos y manejan sus propios errores
    const url = original.url ?? '';
    const isAuthEndpoint = /\/auth\/(login|register|refresh|verify|resend|forgot|reset)/.test(url);
    if (isAuthEndpoint || err.response?.status !== 401 || original._retry) {
      return Promise.reject(err);
    }

    // Si ya hay un refresh en curso, encolar esta solicitud
    if (isRefreshing) {
      return new Promise<unknown>(resolve => {
        pendingQueue.push((token: string) => {
          original._retry = true;
          original.headers.Authorization = `Bearer ${token}`;
          resolve(api(original));
        });
      });
    }

    original._retry = true;
    const newToken = await doRefresh();
    if (!newToken) return Promise.reject(err);
    original.headers.Authorization = `Bearer ${newToken}`;
    return api(original);
  },
);

// ── Auth ─────────────────────────────────────────────────────────────────────
export const authApi = {
  login:          (data: { email: string; password: string }) =>
    api.post<{ accessToken: string; expiresIn: number; user: import('@/types/api').UserDto; hasCompanies: boolean }>('/auth/login', data),
  googleAuth:     (idToken: string) =>
    api.post<{ accessToken: string; expiresIn: number; user: import('@/types/api').UserDto; hasCompanies: boolean }>('/auth/google', { idToken }),
  register:       (data: { email: string; password: string; firstName: string; lastName: string; tenantName: string }) =>
    api.post('/auth/register', data),
  forgotPassword:     (email: string) =>
    api.post('/auth/forgot-password', { email }),
  resetPassword:      (token: string, newPassword: string) =>
    api.post('/auth/reset-password', { token, newPassword }),
  verifyEmail:        (token: string) =>
    api.post('/auth/verify-email', { token }),
  resendVerification: (email: string) =>
    api.post('/auth/resend-verification', { email }),
  me:             () =>
    api.get<import('@/types/api').UserDto>('/auth/me'),
  logout:         () => {
    const { refreshToken } = getStore();
    return api.post('/auth/logout', refreshToken ? { refreshToken } : {});
  },
};

// ── Users ─────────────────────────────────────────────────────────────────────
export const usersApi = {
  updateProfile:   (data: { firstName: string; lastName: string; phone?: string | null }) =>
    api.put('/users/me', data),
  uploadAvatar:    (file: File) => {
    const fd = new FormData();
    fd.append('avatar', file);
    return api.post<import('@/types/api').UserDto>('/users/me/avatar', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  changePassword:  (data: { currentPassword: string; newPassword: string; confirmPassword: string }) =>
    api.put('/users/me/password', data),
  getNotifPrefs:   () =>
    api.get('/users/me/notification-preferences'),
  updateNotifPrefs: (data: unknown) =>
    api.patch('/users/me/notification-preferences', data),
  getSessions:     () =>
    api.get('/users/me/sessions'),
  revokeSession:   (sessionId: string) =>
    api.delete(`/users/me/sessions/${sessionId}`),
  revokeAllSessions: () =>
    api.delete('/users/me/sessions'),
};

// ── Tenants ───────────────────────────────────────────────────────────────────
export const tenantsApi = {
  list:          () => api.get<import('@/types/api').TenantSummary[]>('/tenants/me'),
  create:        (data: { name: string }) => api.post('/tenants', data),
  members:       (id: string) => api.get(`/tenants/${id}/members`),
  getMembers:    (id: string) => api.get<import('@/types/api').TenantMemberDto[]>(`/tenants/${id}/members`),
  createMember:  (id: string, data: { email: string; firstName: string; lastName: string; phone?: string; role: string; companyIds: string[]; canManageCobranzas?: boolean }) =>
    api.post(`/tenants/${id}/members`, data),
  updateMember:  (id: string, userId: string, data: { role?: string; companyIds?: string[]; canManageCobranzas?: boolean }) =>
    api.patch(`/tenants/${id}/members/${userId}`, data),
  removeMember:  (id: string, userId: string) => api.delete(`/tenants/${id}/members/${userId}`),
  getProfile:    (id: string) => api.get<import('@/types/api').TenantProfileDto>(`/tenants/${id}/profile`),
  updateProfile: (id: string, data: Partial<import('@/types/api').TenantProfileDto>) => api.patch(`/tenants/${id}/profile`, data),
  uploadLogo:    (id: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ url: string; key: string }>(`/tenants/${id}/profile/logo`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadCpcFoto: (id: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ url: string; key: string }>(`/tenants/${id}/profile/cpc-foto`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  lookupBillingDocument: (id: string, numero: string) =>
    api.get<{
      success: boolean;
      data: {
        tipoDocumento: 'DNI' | 'RUC';
        razonSocial?:  string;
        direccion?:    string;
        estado?:       string;
      } | null;
      message?: string;
    }>(`/tenants/${id}/lookup-document/${numero}`),
};

// ── Companies ─────────────────────────────────────────────────────────────────
export const companiesApi = {
  list:              () => api.get<import('@/types/api').CompanyDto[]>('/companies'),
  create:            (data: unknown) => api.post('/companies', data),
  update:            (id: string, data: unknown) => api.put(`/companies/${id}`, data),
  delete:            (id: string) => api.delete(`/companies/${id}`),
  deletePermanently: (id: string) => api.delete<void>(`/companies/${id}/permanent`),
  reactivate:        (id: string) => api.patch(`/companies/${id}/reactivate`),
  saveCredentials:   (id: string, data: { rucUser: string; solPassword: string }) =>
    api.post<{ configured: boolean; syncJobId?: string }>(`/companies/${id}/credentials`, data),
  validateCredentials: (id: string, data: { rucUser: string; solPassword: string }) =>
    api.post<{ valid: boolean; message: string }>(`/companies/${id}/credentials/validate`, data),
  saveAfpNetCredentials: (id: string, data: { ruc: string; usuario: string; password: string; captcha?: string; challengeId?: string }) =>
    api.post<{ configured: boolean; message: string; syncJobId?: string }>(`/companies/${id}/afp-net-credentials`, data),
  validateAfpNetCredentials: (id: string, data: { ruc: string; usuario: string; password: string; captcha?: string; challengeId?: string }) =>
    api.post<{ valid: boolean; message: string; captchaChallenge?: import('@/types/api').AfpNetCaptchaChallenge }>(`/companies/${id}/afp-net-credentials/validate`, data),
  resolveAfpNetCaptcha: (id: string, data: { challengeId: string; captcha: string }) =>
    api.post<{ resolved: boolean; message: string }>(`/companies/${id}/afp-net/captcha/resolve`, data),
  saveApiToken:      (id: string, data: { clientId: string; secretToken: string }) =>
    api.post<{ configured: boolean }>(`/companies/${id}/api-token`, data),
  syncApiTokenFromSol: (id: string) =>
    api.post<{ configured: boolean; message: string }>(`/companies/${id}/api-token/sync-from-sol`),
  lookupRuc: (ruc: string) =>
    api.get<{
      success: boolean;
      data: {
        razonSocial?: string;
        estado?:      string;
        tipoPersona?: string;
        direccion?:   string;
        email?:       string;
        telefono?:    string;
        raw: unknown;
      } | null;
      message?: string;
    }>(`/companies/lookup-ruc/${ruc}`),
  regenerateWebhook: (id: string) => api.post(`/companies/${id}/webhook/regenerate`),
  getWebhook:        (id: string) => api.get(`/companies/${id}/webhook`),
  uploadLogo:        (id: string, file: File) => {
    const fd = new FormData();
    fd.append('logo', file);
    return api.post<import('@/types/api').CompanyDto>(`/companies/${id}/logo`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  complianceSummary: (periodo: string) =>
    api.get<import('@/types/api').CompanyComplianceSummaryDto[]>('/companies/compliance-summary', { params: { periodo } }),
  getObligaciones:   (id: string) =>
    api.get<import('@/types/api').CompanyObligacionDto[]>(`/companies/${id}/obligaciones`),
  upsertObligacion:  (id: string, data: { obligationType: string; label: string; isActive?: boolean; sortOrder?: number }) =>
    api.post<import('@/types/api').CompanyObligacionDto>(`/companies/${id}/obligaciones`, data),
  deleteObligacion:  (id: string, obligationType: string) =>
    api.delete<{ deleted: boolean }>(`/companies/${id}/obligaciones/${obligationType}`),
  notifyPreview: (id: string, type: 'RESUMEN' | 'BIENVENIDA', period?: string) =>
    api.get<{ text: string; to: string; templateName: string; sentThisMonth: number }>(
      `/companies/${id}/notify/preview`, { params: { type, ...(period ? { period } : {}) } },
    ),
  notifySend: (id: string, data: { type: 'RESUMEN' | 'BIENVENIDA'; period?: string; customTo?: string; customText?: string }) =>
    api.post<{ sent: boolean; to: string; notificationId: string }>(`/companies/${id}/notify`, data),
};

// ── AFP Net — Planillas ───────────────────────────────────────────────────────
export const afpNetApi = {
  planillas: (id: string, year: string, forceRefresh = false, allowLogin = false) =>
    api.get<{
      items: import('@/types/api').AfpNetPlanillaDto[]; total: number; year: string;
      fromCache: boolean; sessionUsed?: boolean; needsLogin?: boolean;
    }>(
      `/companies/${id}/afp-net/planillas`,
      { params: { year, forceRefresh: forceRefresh ? 'true' : undefined, allowLogin: allowLogin ? 'true' : undefined } },
    ),
  planillaPdf: (id: string, planillaId: string) =>
    api.get(`/companies/${id}/afp-net/planillas/${planillaId}/pdf`, { responseType: 'arraybuffer' }),
};

// ── Domain: company-scoped ────────────────────────────────────────────────────
export const declarationsApi = {
  list:          (cId: string, p?: Record<string, string>) => api.get(`/companies/${cId}/declarations`, { params: p }),
  getByPeriodo:  (cId: string, periodo: string) => api.get(`/companies/${cId}/declarations/${periodo}`),
  decPagos:      (cId: string, year: string, forceRefresh = false) =>
    api.get(`/companies/${cId}/declarations/sunat/dec-pagos`, { params: { year, ...(forceRefresh && { forceRefresh: 'true' }) } }),
  clearDecPagos: (cId: string, year: string) =>
    api.delete(`/companies/${cId}/declarations/sunat/dec-pagos`, { params: { year } }),
  decPagoDetalle: (cId: string, numOrd: string, codigoEcm: string) =>
    api.get<{ base64: string }>(`/companies/${cId}/declarations/sunat/dec-pagos/detalle`, { params: { numOrd, codigoEcm } }),
  decPagoConstancia: (cId: string, numOrd: string, codigoEcm: string) =>
    api.get<{ base64: string }>(`/companies/${cId}/declarations/sunat/dec-pagos/constancia`, { params: { numOrd, codigoEcm } }),
  decPagoTributos: (cId: string, numOrd: string) =>
    api.get(`/companies/${cId}/declarations/sunat/dec-pagos/tributos`, { params: { numOrd } }),

  // ── Reporte gerencial ────────────────────────────────────────────────────
  reporteGerencial: (cId: string, from: string, to: string) =>
    api.get<ReporteGerencialDto>(`/companies/${cId}/declarations/reporte-gerencial`, { params: { from, to } }),

  reporteGerencialCalcTim: (
    cId:        string,
    factualiz:  string,   // dd/mm/yyyy
    items:      { tributo: string; periodo: string; importe: string; pagos?: { fecha: string; importe: string }[] }[],
  ) =>
    api.post<CalcTimLoteResult[]>(
      `/companies/${cId}/declarations/reporte-gerencial/calcular-tim`,
      { factualiz, items },
    ),

  // ── Liquidación tributaria (análisis gerencial 0621 + 0601) ───────────────
  liquidacion: (cId: string, from: string, to: string) =>
    api.get<LiquidacionTributariaDto>(`/companies/${cId}/declarations/liquidacion`, { params: { from, to } }),

  liquidacionExcel: (cId: string, from: string, to: string) =>
    api.get<ArrayBuffer>(`/companies/${cId}/declarations/liquidacion/excel`, {
      params: { from, to }, responseType: 'arraybuffer',
    }),

  // Datos manuales editables (AFP, SCTR, CTS, gratificaciones, etc.)
  liquidacionManualList: (
    cId: string, tipo: 'IGV_RENTA' | 'LABORALES', from: string, to: string,
  ) =>
    api.get<LiquidacionManualEntryDto[]>(`/companies/${cId}/declarations/liquidacion/manual`, {
      params: { tipo, from, to },
    }),

  liquidacionManualUpsert: (
    cId: string,
    body: { periodo: string; tipo: 'IGV_RENTA' | 'LABORALES'; data: LiquidacionManualData },
  ) =>
    api.post<LiquidacionManualEntryDto>(`/companies/${cId}/declarations/liquidacion/manual`, body),

  // ── Notificación WhatsApp de declaración / pago ───────────────────────────
  decNotifyPreview: (
    cId:   string,
    input: {
      numOrd: string; perTri: string; codFor: string; desFor: string;
      strFecPres: string; strFecPago?: string | null; mtoPag: number;
      nomEntFin?: string | null; type: 'DECLARACION' | 'PAGO' | 'DECLARACION_Y_PAGO';
      tributos?: { codTri: string; descCodTriCorta: string; mtoTotDeu: number; mtoPagtot: number }[];
    },
  ) => {
    const { tributos, ...rest } = input;
    return api.get<{ text: string; to: string; templateName: string; sentThisMonth: number }>(
      `/companies/${cId}/declarations/notify/preview`,
      { params: { ...rest, mtoPag: String(rest.mtoPag), ...(tributos?.length ? { tributos: JSON.stringify(tributos) } : {}) } },
    );
  },

  decNotifySend: (
    cId:   string,
    input: {
      numOrd: string; perTri: string; codFor: string; desFor: string;
      strFecPres: string; strFecPago?: string | null; mtoPag: number;
      nomEntFin?: string | null; type: 'DECLARACION' | 'PAGO' | 'DECLARACION_Y_PAGO';
      customTo: string; customText: string;
      tributos?: { codTri: string; descCodTriCorta: string; mtoTotDeu: number; mtoPagtot: number }[];
    },
    files?: File[],
  ) => {
    const fd = new FormData();
    const { tributos, ...rest } = input;
    Object.entries({ ...rest, mtoPag: String(rest.mtoPag) }).forEach(([k, v]) => {
      if (v != null) fd.append(k, String(v));
    });
    if (tributos?.length) fd.append('tributos', JSON.stringify(tributos));
    files?.forEach(f => { fd.append('attachments', f); });
    return api.post<{ sent: boolean; to: string; notificationId: string }>(
      `/companies/${cId}/declarations/notify`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },

  // ── Operaciones sustentadas de pago ────────────────────────────────────────
  listOperaciones: (cId: string, numOrd: string) =>
    api.get<DecPagoOperacionDto[]>(`/companies/${cId}/declarations/operaciones`, { params: { numOrd } }),

  createOperacion: (
    cId:  string,
    data: { numOrd: string; tipo: string; monto: number; descripcion: string; fechaDocumento: string },
    files?: File[],
  ) => {
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => { fd.append(k, String(v)); });
    files?.forEach(f => { fd.append('attachments', f); });
    return api.post<DecPagoOperacionDto>(
      `/companies/${cId}/declarations/operaciones`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },

  deleteOperacion: (cId: string, operacionId: string) =>
    api.delete(`/companies/${cId}/declarations/operaciones/${operacionId}`),

  getOperacionDocUrl: (cId: string, operacionId: string, docId: string) =>
    api.get<{ url: string }>(`/companies/${cId}/declarations/operaciones/${operacionId}/docs/${docId}/url`),
};

export interface LiquidacionManualData {
  // IGV-Renta
  itanManual?:      number;
  creditoExtra?:    number;
  pagoExtra?:       number;
  // Laborales
  afp?:             number;
  sctr?:            number;
  cts?:             number;
  gratificaciones?: number;
  vacaciones?:      number;
  bonos?:           number;
  otrosBeneficios?: number;
  // Común
  notas?:           string;
}

export interface LiquidacionManualEntryDto {
  id:        string;
  companyId: string;
  periodo:   string;
  tipo:      'IGV_RENTA' | 'LABORALES';
  data:      LiquidacionManualData;
  createdAt: string;
  updatedAt: string;
}

// ── Tipos de la liquidación tributaria ────────────────────────────────────

export interface LiquidacionIgvBlock {
  baseVentas18: number; baseVentasAmazonia: number; igvVentas: number;
  ventasNoGravadas: number; ventasExportacion: number; totalVentas: number;
  baseCompras18: number; baseComprasAmazonia: number; igvCompras: number;
  comprasNoGravadas: number; totalCompras: number;
  impuestoResultante: number; saldoAnterior: number; resultanteOSaldoFavor: number;
  compensacionExportador: number; subtotal: number; pagosPrevios: number;
  intereses: number; totalDeuda: number;
}
export interface LiquidacionRentaBlock {
  ingresosNetos: number; pagoCuenta: number; impuestoResultante: number;
  saldoAnterior: number; resultanteOSaldoFavor: number;
  coeficiente: number; porcentaje: number; coeficienteCalc: number;
  itan: number; subtotal: number; pagosPrevios: number;
  intereses: number; totalDeuda: number; pagoCuentaEnExceso: number;
}
export interface LiquidacionPercRet {
  delPeriodo: number; saldoAnterior: number; total: number;
  usadoEnPeriodo: number; saldoNoAplicado: number;
}
export interface LiquidacionDetracciones {
  saldoAnterior: number; delPeriodo: number; total: number;
  pagosIR: number; pagosIGV: number; pagosEsSalud: number;
  pagosSIS: number; pagosONP: number; saldoFinal: number;
}
export interface LiquidacionPlame {
  trabajadores: number; remuneraciones: number; totalAPagar: number;
  essalud: number; sis: number; onp: number; afp: number;
}
export type PagoGrupo  = 'IGV' | 'RENTA' | 'ESSALUD' | 'SIS' | 'ONP' | 'OTRO';
export type PagoOrigen = 'BOLETA' | 'DJ';

export interface PagoDetalleItem {
  numOrd:          string;
  fecha:           string | null;   // dd/mm/yyyy
  codTri:          string;
  descCodTri:      string;
  descCodTriCorta: string;
  monto:           number;
  grupo:           PagoGrupo;
  origen:          PagoOrigen;      // BOLETA = 1662 ; DJ = pago directo en 0621/0601
  codFor:          string;
}

export interface LiquidacionPagos {
  totalIgv: number; totalRenta: number; totalEsSalud: number;
  totalSis: number; totalOnp: number; totalGeneral: number; ultimaFecha: string | null;
  detalle:  PagoDetalleItem[];
}
export interface PeriodoLiquidacionDto {
  periodo: string; periodoLabel: string;
  tieneDj0621: boolean; tieneDj0601: boolean;
  tipoDeclaracion0621: 'ORIGINAL' | 'RECTIFICATORIA' | 'SUSTITUTORIA' | null;
  rectificatoriasCount: number;
  igv: LiquidacionIgvBlock; renta: LiquidacionRentaBlock;
  percepciones: LiquidacionPercRet; retenciones: LiquidacionPercRet;
  percepcionesVentas: LiquidacionPercRet; retencionesCompras: LiquidacionPercRet;
  detracciones: LiquidacionDetracciones; plame: LiquidacionPlame;
  pagos: LiquidacionPagos;
  totalConsolidado: {
    igv: number; renta: number; essalud: number; sis: number; onp: number; afp: number; total: number;
  };
}
export interface LiquidacionTributariaDto {
  empresa:  { id: string; ruc: string; razonSocial: string };
  rango:    { from: string; to: string };
  periodos: PeriodoLiquidacionDto[];
  kpis: {
    igvDeclarado: number; igvPagado: number;
    rentaDeclarada: number; rentaPagada: number;
    saldoFavorIgvActual: number;
    saldoPercepcionesNoAplicadas: number;
    saldoRetencionesNoAplicadas: number;
    saldoSpotActual: number;
    periodosConSaldoAFavor: number;
    periodosConPagoPendiente: number;
  };
}

// ── Tipos del reporte gerencial ────────────────────────────────────────────

export interface ReporteGerencialTributo {
  codTri:          string;
  descCodTri:      string;
  descCodTriCorta: string;
  mtoTotDeu:       number;
  mtoPagtot:       number;
  mtoResPag:       number;
  perTri:          string;
  pendienteReal:   number;
  pagosEnlazados:  { numOrd: string; codFor: string; monto: number; fecPago: string | null }[];
}

export interface ReporteGerencialDjRow {
  dj: {
    numOrd: string; codFor: string; desFor: string; perTri: string;
    strFecPres: string; ecmFormulario: string; ecmCasillas: string;
    tipoForm: string; indBoleta: string; numOrdOri: string;
    casillas: Record<string, string>; mtoPag: number;
  };
  tributos:         ReporteGerencialTributo[];
  totalDeclarado:   number;
  totalPagado:      number;
  totalPendiente:   number;
  itemsParaTim:     { tributo: string; periodo: string; importe: string; descCorta: string }[];
}

export interface ReporteGerencialFraa36 {
  numOrd:     string;
  codFor:     string;
  desFor:     string;
  perTri:     string;
  fecPago:    string | null;
  strFecPres: string;
  mtoPag:     number;
  tributos:   ReporteGerencialTributo[];
}

export interface ReporteGerencialDto {
  empresa: { id: string; ruc: string; razonSocial: string };
  rango:   { from: string; to: string };
  resumen: {
    totalDeclarado: number;
    totalPagado:    number;
    totalPendiente: number;
    djsCount:       number;
    fraa36Count:    number;
  };
  porPeriodo: {
    periodo:        string;
    totalDeclarado: number;
    totalPagado:    number;
    totalPendiente: number;
  }[];
  porTributo: {
    codTri:          string;
    descCodTri:      string;
    descCodTriCorta: string;
    totalDeclarado:  number;
    totalPagado:     number;
    totalPendiente:  number;
  }[];
  declaraciones: ReporteGerencialDjRow[];
  fraa36:        ReporteGerencialFraa36[];
}

export interface CalcTimLoteResult {
  tributo:           string;
  periodo:           string;
  importe:           string;
  totalDeuda:        number;
  importeOriginal:   number;
  intereses:         number;
  fechaVencimiento:  string | null;
  fechaActualiz:     string;
  fromCache:         boolean;
  error?:            string;
}
export const paymentsApi = {
  list:          (cId: string, p?: Record<string, string>) => api.get(`/companies/${cId}/payments`, { params: p }),
};
export const detractionsApi = {
  list:              (cId: string, p?: Record<string, string>) => api.get(`/companies/${cId}/detractions`, { params: p }),
  saldo:             (cId: string) => api.get(`/companies/${cId}/detractions/saldo`),
  consulta:          (cId: string, year: string, forceRefresh = false) =>
    api.get(`/companies/${cId}/detractions/consulta`, { params: { year, ...(forceRefresh && { forceRefresh: 'true' }) } }),
  constanciaPreview: (cId: string, constancia: string) =>
    api.get<{ html: string }>(`/companies/${cId}/detractions/constancia`, { params: { constancia } }),
  clear: (cId: string, year: string) =>
    api.delete(`/companies/${cId}/detractions/consulta`, { params: { year } }),

  // ── Reporte gerencial — Estado de cuenta SPOT ─────────────────────────────
  reporteGerencial: (cId: string, from: string, to: string, criterio: 'FECHA_PAGO' | 'PERIODO' = 'FECHA_PAGO') =>
    api.get<ReporteDetraccionesGerencialDto>(`/companies/${cId}/detractions/reporte-gerencial`, { params: { from, to, criterio } }),
  getSaldoInicial: (cId: string, anio: number) =>
    api.get<DetractionSaldoInicialDto | null>(`/companies/${cId}/detractions/saldo-inicial`, { params: { anio } }),
  upsertSaldoInicial: (cId: string, body: { anio: number; saldo: number; fecha: string }) =>
    api.put<DetractionSaldoInicialDto>(`/companies/${cId}/detractions/saldo-inicial`, body),
};

export const fraccionamiento36Api = {
  /** DB-first — nunca dispara una consulta a SUNAT. */
  get: (cId: string) =>
    api.get<F36ResumenDto>(`/companies/${cId}/fraccionamiento36`),
  /** Consulta EN VIVO en SUNAT — puede tardar hasta 3 minutos. Roles ADMIN/OWNER. */
  consultar: (cId: string) =>
    api.post<F36ResumenDto>(
      `/companies/${cId}/fraccionamiento36/consultar`, undefined, { timeout: 185_000 },
    ),
};

// ── Tipos del reporte gerencial de detracciones ────────────────────────────

export interface DetractionSaldoInicialDto {
  anio:  number;
  saldo: number;
  fecha: string;
}

export interface ReporteDetraccionesGerencialDto {
  empresa: { id: string; ruc: string; razonSocial: string };
  cuenta:  { numCuenta: string | null; banco: string | null };
  rango:   { from: string; to: string; criterio: 'FECHA_PAGO' | 'PERIODO' };
  saldoInicial: {
    presente: boolean;
    anio:     number;
    saldo:    number;
    fecha:    string | null;
  };
  resumen: {
    saldoInicial:        number;
    totalEntradas:       number;
    totalSalidas:        number;
    saldoFinalCalculado: number;
    countEntradas:       number;
    countSalidas:        number;
  };
  movimientos: {
    fecha:           string;
    tipo:            'ENTRADA' | 'SALIDA';
    concepto:        string;
    documento:       string | null;
    contraparte:     string | null;
    monto:           number;
    saldoAcumulado:  number;
    refId:           string;
  }[];
  resumenMensual: {
    periodo:     string;
    entradas:    number;
    salidas:     number;
    saldoFinal:  number;
  }[];
  topClientes: {
    ruc:           string;
    razonSocial:   string;
    totalDeposito: number;
    cantidad:      number;
  }[];
  salidasPorTributo: {
    codFor:    string;
    desFor:    string;
    total:     number;
    cantidad:  number;
  }[];
  tributosPagadosConBoletas: {
    codTri:          string;
    descCodTri:      string;
    descCodTriCorta: string;
    total:           number;
    cantidad:        number;
  }[];
}
export const retentionsApi = {
  list:          (cId: string, p?: Record<string, string>) => api.get(`/companies/${cId}/retentions`, { params: p }),
  aplicables:    (cId: string) => api.get(`/companies/${cId}/retentions/aplicables`),
};
export const debtsApi = {
  list:             (cId: string, p?: Record<string, string>) => api.get(`/companies/${cId}/debts`, { params: p }),
  totalActualizado: (cId: string) => api.get(`/companies/${cId}/debts/total-actualizado`),
  recalculate:      (cId: string, debtId: string) => api.post(`/companies/${cId}/debts/${debtId}/recalculate`),
  consolidated:     (cId: string, from: string, to: string, fechaCorte?: string) =>
    api.get<DeudasConsolidadasDto>(`/companies/${cId}/debts/consolidated`, {
      params: { from, to, ...(fechaCorte ? { fechaCorte } : {}) },
    }),
  detalle:          (cId: string, key: string, fechaCorte?: string) =>
    api.get<DeudaDetalleDto>(`/companies/${cId}/debts/detail/${encodeURIComponent(key)}`, {
      params: fechaCorte ? { fechaCorte } : undefined,
    }),
};

// ── Deudas consolidadas — tipos ──────────────────────────────────────────────
export type FuenteDeuda = 'TRIBUTO' | 'MULTA' | 'OMISION' | 'SIN_CRONOGRAMA';
export type DeudaStatus = 'VENCIDA' | 'PROXIMA' | 'VIGENTE' | 'FUTURA' | 'SIN_CRONOGRAMA';

export interface DeudaConsolidadaDto {
  key:                 string;
  fuente:              FuenteDeuda;
  refId:               string;
  numOrd:              string | null;
  codFor:              string | null;
  desFor:              string;
  obligationType:      string;
  periodo:             string;
  codTri:              string | null;
  concepto:            string;
  conceptoLargo:       string;
  fechaVencimiento:    string | null;
  fechaCorte:          string;
  diasVencido:         number;
  montoOriginal:       number;
  montoPagado:         number;
  saldoPendiente:      number;
  intereses:           number;
  totalActualizado:    number;
  timUsada:            { mensual: number; diaria: number };
  status:              DeudaStatus;
  pagosCount:          number;
  calculadoraOk:       boolean;
}

export interface DeudasConsolidadasDto {
  empresa:    { id: string; ruc: string; razonSocial: string; ultimoDigitoRuc: string };
  rango:      { from: string; to: string };
  fechaCorte: string;
  resumen: {
    fechaCorte:        string;
    totalRegistros:    number;
    totalOriginal:     number;
    totalIntereses:    number;
    totalActualizado:  number;
    porFuente: {
      tributos:      { count: number; original: number; actualizado: number };
      multas:        { count: number; original: number; actualizado: number };
      omisiones:     { count: number; original: number; actualizado: number };
      sinCronograma: { count: number };
    };
    vencidasCount:     number;
    vencidasMonto:     number;
  };
  deudas:     DeudaConsolidadaDto[];
}

export interface PagoParcialDto {
  numOrd:        string;
  codFor:        string;
  desFor:        string;
  banco:         string;
  fechaPago:     string | null;
  fechaPres:     string;
  mtoPagtot:     number;
  tipoFormLabel: string;
}

export interface DesgloseCalculoDto {
  saldoPendiente:   number;
  fechaVencimiento: string | null;
  fechaCorte:       string;
  diasVencido:      number;
  tim:              { mensual: number; diaria: number };
  factor:           number;
  intereses:        number;
  totalActualizado: number;
}

export interface ProyeccionDto {
  fecha:            string;
  diasDesdeCorte:   number;
  diasDesdeVenc:    number;
  intereses:        number;
  totalActualizado: number;
}

export interface DeudaDetalleDto {
  key:        string;
  resumen: {
    fuente:           string;
    concepto:         string;
    conceptoLargo:    string;
    obligationType:   string;
    periodo:          string;
    numOrd:           string | null;
    codTri:           string | null;
    desFor:           string;
    fechaVencimiento: string | null;
  };
  calculo:        DesgloseCalculoDto;
  pagos:          PagoParcialDto[];
  proyecciones:   ProyeccionDto[];
  ecmFormulario:  string | null;
}
export const finesApi = {
  list:          (cId: string, p?: Record<string, string>) => api.get(`/companies/${cId}/fines`, { params: p }),
  total:         (cId: string) => api.get(`/companies/${cId}/fines/total`),
  simulate:      (cId: string, data: unknown) => api.post(`/companies/${cId}/fines/simulate`, data),
  markSubsanada: (cId: string, fineId: string) => api.post(`/companies/${cId}/fines/${fineId}/mark-subsanada`),
};
export const obligationsApi = {
  list:          (cId: string, p?: Record<string, string>) => api.get(`/companies/${cId}/obligations`, { params: p }),
  proximas:      (cId: string) => api.get(`/companies/${cId}/obligations/proximas`),
  omisas:        (cId: string) => api.get(`/companies/${cId}/obligations/omisas`),
  regenerate:    (cId: string, year: number) => api.post(`/companies/${cId}/obligations/regenerate`, { year }),
};
export const buzonApi = {
  list:               (cId: string, p?: Record<string, unknown>) =>
    api.get<import('@/types/api').Paginated<import('@/types/api').BuzonItemDto> & { items: import('@/types/api').BuzonItemDto[]; total: number }>(`/companies/${cId}/buzon`, { params: p }),
  getById:            (itemId: string) =>
    api.get<import('@/types/api').BuzonItemDto>(`/buzon/${itemId}`),
  criticos:           (cId: string) =>
    api.get(`/companies/${cId}/buzon/criticos`),
  sync:               (cId: string) =>
    api.post<{ synced: number; total: number }>(`/companies/${cId}/buzon/sync`),
  readSunat:          (cId: string, itemId: string) =>
    api.post<import('@/types/api').BuzonReadResultDto>(`/companies/${cId}/buzon/${itemId}/read-sunat`),
  downloadAttachment: (cId: string, itemId: string, data: { fileCode: string; alertIndex?: string; messageType?: string; filename?: string }) =>
    api.post(`/companies/${cId}/buzon/${itemId}/attachment`, data, { responseType: 'arraybuffer' }),
  reporteGerencial:   (cId: string, from: string, to: string) =>
    api.get<ReporteBuzonGerencialDto>(`/companies/${cId}/buzon/reporte-gerencial`, { params: { from, to } }),
};

// ── Tipos reporte gerencial Buzón ──────────────────────────────────────────

export interface ReporteBuzonGerencialDto {
  empresa: { id: string; ruc: string; razonSocial: string };
  rango:   { from: string; to: string };
  resumen: {
    totalMensajes:    number;
    noLeidos:         number;
    criticos:         number;
    criticosNoLeidos: number;
    porcentajeLeidos: number;
    conAdjuntos:      number;
  };
  porCategoria: { categoria: string; total: number; noLeidos: number }[];
  porEtiqueta:  {
    labelCode: string; label: string; total: number; noLeidos: number; esCritica: boolean;
  }[];
  evolucionMensual: { periodo: string; total: number; criticos: number; noLeidos: number }[];
  topRemitentes:    { remitente: string; total: number; noLeidos: number }[];
  criticosNoLeidos: {
    id: string; messageCode: string | null; asunto: string; remitente: string;
    fechaEnvio: string; labelCode: string | null; label: string | null; categoria: string;
  }[];
}
export const complianceApi = {
  score:            (cId: string, periodo: string) => api.get(`/companies/${cId}/compliance/score`, { params: { periodo } }),
  scoreAnual:       (cId: string, year: number) => api.get(`/companies/${cId}/compliance/score-anual`, { params: { year } }),
  analisisIntegral: (cId: string, year?: number) => api.get(`/companies/${cId}/compliance/analisis-integral`, { params: year ? { year } : {} }),
};
// ── SIRE ──────────────────────────────────────────────────────────────────────
export const sireApi = {
  reporte:  (cId: string, codLibro: string, period?: string, forceRefresh = false) =>
    api.get(`/companies/${cId}/sire/reporte-cumplimiento`, { params: { codLibro, ...(period && { period }), ...(forceRefresh && { forceRefresh: 'true' }) } }),
  constancias: (cId: string, codLibro: string, period?: string, forceRefresh = false) =>
    api.get(`/companies/${cId}/sire/constancias`, { params: { codLibro, ...(period && { period }), ...(forceRefresh && { forceRefresh: 'true' }) } }),
  pdfConstancia: (cId: string, filename: string) =>
    api.get(`/companies/${cId}/sire/constancias/pdf`, { params: { filename }, responseType: 'arraybuffer' }),
  historial: (cId: string, codLibro: string, period?: string, forceRefresh = false) =>
    api.get(`/companies/${cId}/sire/historial`, { params: { codLibro, ...(period && { period }), ...(forceRefresh && { forceRefresh: 'true' }) } }),
  historialExcel: (cId: string, codLibro: string, period?: string) =>
    api.get(`/companies/${cId}/sire/historial/excel`, { params: { codLibro, ...(period && { period }) }, responseType: 'arraybuffer' }),
  consolidado: (cId: string, codLibro: string, perTributario: string, codTipoRegistro: string, forceRefresh = false) =>
    api.get(`/companies/${cId}/sire/consolidado`, { params: { codLibro, perTributario, codTipoRegistro, ...(forceRefresh && { forceRefresh: 'true' }) } }),
  consolidadoExcel: (cId: string, codLibro: string, perTributario: string, codTipoRegistro: string) =>
    api.get(`/companies/${cId}/sire/consolidado/excel`, { params: { codLibro, perTributario, codTipoRegistro }, responseType: 'arraybuffer' }),
  clearCache: (cId: string, tipo: string, codLibro: string, period: string) =>
    api.delete(`/companies/${cId}/sire/cache`, { params: { tipo, codLibro, period } }),
  reporteGerencial: (cId: string, from: string, to: string) =>
    api.get<ReporteSireGerencialDto>(`/companies/${cId}/sire/reporte-gerencial`, { params: { from, to } }),
  notifyPreview: (cId: string, perTri: string, type: 'RVIE' | 'RCE') =>
    api.get<{ text: string; to: string; templateName: string; sentThisMonth: number; constanciaFilename: string | null }>(
      `/companies/${cId}/sire/notify/preview`, { params: { perTri, type } },
    ),
  notifySend: (cId: string, input: { perTri: string; type: string; customTo: string; customText: string }, file?: File) => {
    const fd = new FormData();
    fd.append('perTri',      input.perTri);
    fd.append('type',        input.type);
    fd.append('customTo',    input.customTo);
    fd.append('customText',  input.customText);
    if (file) fd.append('files', file, file.name);
    return api.post(`/companies/${cId}/sire/notify`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// ── Tipos reporte gerencial SIRE ───────────────────────────────────────────

export type EstadoCumplimientoSire =
  | 'CUMPLIDO_A_TIEMPO'
  | 'CUMPLIDO_TARDE'
  | 'PARCIAL'
  | 'VENCIDO_SIN_CUMPLIR'
  | 'PENDIENTE'
  | 'SIN_CRONOGRAMA';

export interface ReporteSireGerencialDto {
  empresa: { id: string; ruc: string; razonSocial: string; ultimoDigitoRuc: string };
  rango:   { from: string; to: string };
  cronograma: {
    obligationType: string;
    rucLastDigit:   string;
    periodos:       { periodo: string; vencimiento: string | null }[];
  };
  resumen: {
    periodosTotal:              number;
    periodosEvaluables:         number;
    periodosCumplidosATiempo:   number;
    periodosCumplidosTarde:     number;
    periodosCumplidos:          number;
    periodosParciales:          number;
    periodosVencidosSinCumplir: number;
    periodosFuturos:            number;
    porcentajeCumplimiento:     number;
    porcentajeATiempo:          number;
    totalConstancias:           number;
    constanciasPrincipales:     number;
    constanciasAjustes:         number;
  };
  cumplimientoMensual: {
    periodo:        string;
    periodoLabel:   string;
    vencimiento:    string | null;
    vencimientoFmt: string | null;
    diasParaVencer: number | null;
    estado:         EstadoCumplimientoSire;
    rvie: {
      generado:         boolean;
      aTiempo:          boolean | null;
      diasFueraDePlazo: number | null;
      constanciasCount: number;
      fechGeneracion:   string | null;
      fechGeneracionRaw: string | null;
      numOperacion:     string | null;
    };
    rce: {
      generado:         boolean;
      aTiempo:          boolean | null;
      diasFueraDePlazo: number | null;
      constanciasCount: number;
      fechGeneracion:   string | null;
      fechGeneracionRaw: string | null;
      numOperacion:     string | null;
    };
  }[];
  importesPorPeriodo: {
    periodo:              string;
    libro:                'RVIE' | 'RCE';
    codTipoRegistro:      string;
    descTipoRegistro:     string;
    esAjustePosterior:    boolean;
    cantidadComprobantes: number;
    mtoTotalBIGravada:    number;
    mtoTotalBIExonerada:  number;
    mtoTotalBIInafecta:   number;
    mtoTotalIGV:          number;
    mtoTotalISC:          number;
    mtoTotalIcbp:         number;
    mtoTotalOtrosTrib:    number;
    mtoSumTotalCP:        number;
    mtoTotalValorAdqNG:   number;
  }[];
  totalesImportes: {
    rvie: {
      cantidad: number; mtoBIGravada: number; mtoExonerado: number; mtoInafecto: number;
      mtoIGV: number; mtoISC: number; mtoIcbp: number; mtoOtrosTrib: number; mtoTotalCP: number;
    };
    rce: {
      cantidad: number; mtoBIGravada: number; mtoValorAdqNG: number;
      mtoIGV: number; mtoISC: number; mtoIcbp: number; mtoOtrosTrib: number; mtoTotalCP: number;
    };
  };
  constancias: {
    periodo:            string;
    libro:              'RVIE' | 'RCE';
    numOperacion:       string;
    perTributario:      string | null;
    fecTransaccion:     string | null;
    fecTransaccionFmt:  string | null;
    pdfDisponible:      boolean;
    esAjustePosterior:  boolean;
  }[];
  historialPorTipo: {
    rvie: {
      codTipoRegistro:      string; descTipoRegistro: string;
      esAjustePosterior:    boolean;
      cantidad:             number;
      ultimaTransaccion:    string | null;
      ultimaTransaccionFmt: string | null;
    }[];
    rce:  {
      codTipoRegistro:      string; descTipoRegistro: string;
      esAjustePosterior:    boolean;
      cantidad:             number;
      ultimaTransaccion:    string | null;
      ultimaTransaccionFmt: string | null;
    }[];
  };
}

// ── Parámetros Tributarios (UIT + Cronograma) ─────────────────────────────────
export const taxParamsApi = {
  // UIT
  uitList:   () =>
    api.get<{ id: string; year: number; amount: string; decree: string | null; validFrom: string; createdAt: string }[]>('/tax-params/uit'),
  uitCreate: (data: { year: number; amount: number; decree?: string; validFrom: string }) =>
    api.post('/tax-params/uit', data),
  uitUpdate: (id: string, data: { year: number; amount: number; decree?: string; validFrom: string }) =>
    api.put(`/tax-params/uit/${id}`, data),
  uitDelete: (id: string) =>
    api.delete(`/tax-params/uit/${id}`),
  // Cronograma
  cronogramaList: (year?: number, obligationType?: string) =>
    api.get<{
      id: string; year: number; period: number; rucLastDigit: string;
      dueDate: string; obligationType: string; notes: string | null;
    }[]>('/tax-params/cronograma', { params: { ...(year && { year }), ...(obligationType && { obligationType }) } }),
  cronogramaBulkUpsert: (data: {
    year: number;
    obligationType: string;
    entries: { period: number; rucLastDigit: string; dueDate: string; notes?: string }[];
  }) =>
    api.post<{ upserted: number }>('/tax-params/cronograma/bulk-upsert', data),
  cronogramaDelete: (year: number, obligationType: string) =>
    api.delete<{ deleted: number }>('/tax-params/cronograma', { params: { year, obligationType } }),
};

// ── Tax Catalog — Banco de Multas (Infracciones + Gradualidades) ─────────────

export interface TaxInfractionDto {
  id:                  string;
  code:                string;
  tableType:           'TABLE_I' | 'TABLE_II' | 'TABLE_III';
  article:             number;
  numeral:             number | null;
  literal:             string | null;
  description:         string;
  sanctionType:        string;
  sanctionUit:         string | null;
  sanctionPctTributo:  string | null;
  minSanctionUit:      string | null;
  maxSanctionUit:      string | null;
  notes:               string | null;
  isActive:            boolean;
  gradualityRules?:    GradualityRuleDto[];
  createdAt:           string;
  updatedAt:           string;
}

export interface GradualityRuleDto {
  id:               string;
  infractionId:     string;
  resolution:       string;
  subsanationType:  string;
  reductionPct:     string;
  requiresPayment:  boolean;
  conditions:       string | null;
  isActive:         boolean;
  createdAt:        string;
  infraction?:      { code: string; description: string };
}

export interface UpsertInfractionInput {
  code:                string;
  tableType:           string;
  article:             number;
  numeral?:            number | null;
  literal?:            string | null;
  description:         string;
  sanctionType:        string;
  sanctionUit?:        number | null;
  sanctionPctTributo?: number | null;
  minSanctionUit?:     number | null;
  maxSanctionUit?:     number | null;
  notes?:              string | null;
  isActive?:           boolean;
}

export interface UpsertGradualityRuleInput {
  infractionId:     string;
  resolution:       string;
  subsanationType:  string;
  reductionPct:     number;
  requiresPayment?: boolean;
  conditions?:      string | null;
  isActive?:        boolean;
}

// ── Multas consolidadas — tipos ───────────────────────────────────────────────

export type FuenteMulta = 'OMISION' | 'TARDIA' | 'TRIBUTO_OMITIDO' | 'BUZON';
export type StatusMulta = 'VIGENTE' | 'SUBSANADA' | 'PAGADA' | 'NO_CLASIFICADA';

export interface MultaConsolidadaDto {
  key:               string;
  fuente:            FuenteMulta;
  infractionCode:    string | null;
  infractionDesc:    string;
  articulo:          number;
  numeral:           number | null;
  tableType:         string;
  periodo:           string;
  obligationType:    string;
  fechaInfraccion:   string | null;
  fechaCorte:        string;
  diasVencido:       number;
  uitVigente:        number;
  multaBase:         number;
  gradualidadPct:    number;
  gradualidadTipo:   string | null;
  multaFinal:        number;
  intereses:         number;
  totalACancelar:    number;
  fuenteCalculo:     'SUNAT_CALCULADORA' | 'MOTOR_INTERNO';
  status:            StatusMulta;
  numValor:          string | null;
  buzonItemId:       string | null;
  fineId:            string | null;
  obligacionDetalle: {
    label:             string;
    codFor:            string | null;
    desFor:            string | null;
    numOrd:            string | null;
    fechaVencimiento:  string | null;
    fechaPresentacion: string | null;
    codTri:            string | null;
    tributoDesc:       string | null;
    tributoOmitido:    number | null;
  };
}

export interface MultasConsolidadasDto {
  empresa:    { id: string; ruc: string; razonSocial: string; ultimoDigitoRuc: string; regimen: string };
  rango:      { from: string; to: string };
  fechaCorte: string;
  resumen: {
    fechaCorte:        string;
    totalRegistros:    number;
    totalMultaBase:    number;
    totalMultaFinal:   number;
    totalIntereses:    number;
    totalACancelar:    number;
    porFuente: {
      omisiones:        { count: number; total: number };
      tardias:          { count: number; total: number };
      tributoOmitido:   { count: number; total: number };
      buzonResoluciones:{ count: number; total: number };
    };
    vigentesCount:     number;
  };
  multas: MultaConsolidadaDto[];
}

export interface UpsertFineSnapshotInput {
  articulo:          number;
  numeral:           number | null;
  tableType:         string;
  description:       string;
  periodoInfraccion: string | null;
  multaBase:         number;
  rebajaGradualidad: number;
  multaFinal:        number;
  interesTim:        number;
  totalACancelar:    number;
  uitVigente:        number;
  numValor?:         string | null;
  action:            'MARK_SUBSANADA' | 'MARK_PAGADA';
  fineId?:           string;
  detectedFrom?:     string;
  notes?:            string | null;
}

export const multasApi = {
  consolidated: (cId: string, from: string, to: string, fechaCorte?: string) =>
    api.get<MultasConsolidadasDto>(`/companies/${cId}/multas/consolidated`, {
      params: { from, to, ...(fechaCorte ? { fechaCorte } : {}) },
    }),
  snapshot: (cId: string, data: UpsertFineSnapshotInput) =>
    api.post(`/companies/${cId}/multas/snapshot`, data),
};

export const taxCatalogApi = {
  // Infractions
  listInfractions: (params?: { tableType?: string; article?: number; isActive?: boolean }) =>
    api.get<TaxInfractionDto[]>('/tax-catalog/infractions', { params }),
  getInfraction: (code: string) =>
    api.get<TaxInfractionDto>(`/tax-catalog/infractions/${code}`),
  createInfraction: (data: UpsertInfractionInput) =>
    api.post<TaxInfractionDto>('/tax-catalog/infractions', data),
  updateInfraction: (id: string, data: UpsertInfractionInput) =>
    api.put<TaxInfractionDto>(`/tax-catalog/infractions/${id}`, data),
  deleteInfraction: (id: string) =>
    api.delete(`/tax-catalog/infractions/${id}`),
  // Graduality rules
  listGradualityRules: (params?: { infractionId?: string; resolution?: string; isActive?: boolean }) =>
    api.get<GradualityRuleDto[]>('/tax-catalog/graduality-rules', { params }),
  createGradualityRule: (data: UpsertGradualityRuleInput) =>
    api.post<GradualityRuleDto>('/tax-catalog/graduality-rules', data),
  updateGradualityRule: (id: string, data: UpsertGradualityRuleInput) =>
    api.put<GradualityRuleDto>(`/tax-catalog/graduality-rules/${id}`, data),
  deleteGradualityRule: (id: string) =>
    api.delete(`/tax-catalog/graduality-rules/${id}`),
};

// ── Calculadora Tributaria ────────────────────────────────────────────────────

interface CalcBodyDj {
  con_deuda:  '00';
  tributo:    string;
  periodo:    string;
  importe:    string;
  factualiz:  string;
  pagos?:     { fecha: string; importe: string }[];
}

interface CalcBodyValor {
  con_deuda: '01';
  num_valor: string;
  factualiz: string;
}

type CalcBody = CalcBodyDj | CalcBodyValor;

export const calculadoraApi = {
  /** Cálculo libre — RUC incluido en el body */
  calcular: (data: CalcBody & { ruc: string }) =>
    api.post<{ success: boolean; result: unknown }>('/calculadora/deuda', data),

  /** Cálculo scoped a empresa — RUC se resuelve desde la empresa */
  calcularEmpresa: (cId: string, data: CalcBody) =>
    api.post<{ success: boolean; result: unknown }>(`/companies/${cId}/calculadora/deuda`, data),
};

// ── Cuenta de Control — Retenciones y Percepciones IGV ───────────────────────

export interface CuentaControlItemApi {
  id:                       string;
  tipoConsulta:             string;
  periodo:                  string;
  tipoDocumento:            string;
  nroDocAgente:             string;
  nombreAgente:             string;
  origen:                   string;
  tipoOrigen:               string;
  serie:                    string;
  nroComprobante:           string;
  fechaEmision:             string;
  fechaCancelacion:         string | null;
  estado:                   string;
  fechaReversion:           string | null;
  nroCda:                   string | null;
  montoCobrado:             number;
  montoRetencionPercepcion: number;
}

export interface CuentaControlPeriodResumenApi {
  periodo:                  string;
  montoCobrado:             number;
  montoRetencionPercepcion: number;
}

export interface CuentaControlResult {
  items:        CuentaControlItemApi[];
  /** Totales mensuales que el scraper devuelve siempre (independiente del detalle). */
  resumen?:     CuentaControlPeriodResumenApi[];
  total:        number;
  year:         string;
  tipoConsulta: string;
  fromCache:    boolean;
}

export const cuentaControlApi = {
  consulta: (cId: string, year: string, tipoConsulta: '01' | '02', forceRefresh = false) =>
    api.get<CuentaControlResult>(`/companies/${cId}/cuenta-control/consulta`, {
      params: { year, tipoConsulta, ...(forceRefresh && { forceRefresh: 'true' }) },
    }),
  clear: (cId: string, year: string, tipoConsulta: '01' | '02') =>
    api.delete<{ deleted: number }>(`/companies/${cId}/cuenta-control/consulta`, {
      params: { year, tipoConsulta },
    }),

  reporteGerencial: (cId: string, from: string, to: string, tipoConsulta: '01' | '02') =>
    api.get<ReporteCuentaControlGerencialDto>(
      `/companies/${cId}/cuenta-control/reporte-gerencial`,
      { params: { from, to, tipoConsulta } },
    ),
};

// ── Tipos del reporte gerencial de cuenta-control ──────────────────────────

export interface ReporteCuentaControlGerencialDto {
  empresa:     { id: string; ruc: string; razonSocial: string };
  rango:       { from: string; to: string };
  tipoConsulta:'01' | '02';
  tipoLabel:   string;
  detalleDisponible: boolean;
  resumen: {
    totalCobrado:           number;
    totalRetenidoPercibido: number;
    cantidadDocumentos:     number;
    cantidadAgentes:        number;
    cantidadMeses:          number;
    tasaPromedio:           number;
  };
  resumenMensual: {
    periodo:           string;
    cobrado:           number;
    retenidoPercibido: number;
    cantidad:          number;
    fuente:            'DETALLE' | 'RESUMEN' | 'AMBOS';
  }[];
  topAgentes: {
    nroDocAgente:           string;
    nombreAgente:           string;
    totalCobrado:           number;
    totalRetenidoPercibido: number;
    cantidad:               number;
  }[];
  porOrigen: {
    origen:                 string;
    tipoOrigen:             string;
    totalCobrado:           number;
    totalRetenidoPercibido: number;
    cantidad:               number;
  }[];
  porEstado: {
    estado:                 string;
    totalCobrado:           number;
    totalRetenidoPercibido: number;
    cantidad:               number;
  }[];
}

// ── Admin — Users (MASTER only) ───────────────────────────────────────────────
export const adminUsersApi = {
  list:         () =>
    api.get<import('@/types/api').AdminUserDto[]>('/admin/users'),
  create:       (data: { email: string; password: string; firstName: string; lastName: string; phone?: string; tenantName: string }) =>
    api.post<import('@/types/api').AdminUserDto>('/admin/users', data),
  update:       (id: string, data: { firstName?: string; lastName?: string; phone?: string | null; tenantName?: string; systemRole?: string }) =>
    api.patch<import('@/types/api').AdminUserDto>(`/admin/users/${id}`, data),
  toggleActive: (id: string) =>
    api.patch<{ id: string; isActive: boolean }>(`/admin/users/${id}/toggle-active`),
  deletePermanently: (id: string) =>
    api.delete<void>(`/admin/users/${id}`),
  getSubscriptionHistory: (id: string) =>
    api.get(`/admin/users/${id}/subscription-history`),
};

export const adminWorkerApi = {
  queues: () =>
    api.get<import('@/types/api').WorkerQueuesDto>('/admin/worker/queues'),
  jobs: (queueName: string, status: string, limit = 20) =>
    api.get<import('@/types/api').WorkerJobSummaryDto[]>(`/admin/worker/queues/${queueName}/jobs`, {
      params: { status, limit },
    }),
  runPlanner: () =>
    // El planner recorre 30 días × todas las plantillas programadas del
    // sistema — puede tardar más que el timeout por defecto.
    api.post<import('@/types/api').RunPlannerResultDto>('/admin/worker/run-planner', {}, { timeout: 125_000 }),
};

// ── WhatsApp ──────────────────────────────────────────────────────────────────
export const whatsappApi = {
  list:       () =>
    api.get<import('@/types/api').WhatsappInstanceDto[]>('/whatsapp/instances'),
  create:     (data: { displayName: string; notifyEmail?: string }) =>
    api.post<import('@/types/api').WhatsappInstanceDto>('/whatsapp/instances', data),
  getQr:      (id: string) =>
    api.get<{ base64: string | null }>(`/whatsapp/instances/${id}/qr`),
  disconnect: (id: string) =>
    api.post(`/whatsapp/instances/${id}/disconnect`),
  remove:     (id: string) =>
    api.delete(`/whatsapp/instances/${id}`),
  groups:     (refresh = false) =>
    // timeout más alto que el default (30s): cuentas con muchos grupos activos
    // pueden tardar más en Evolution API — coincide con el timeout del backend (120s).
    // El backend cachea la respuesta 24h en Redis; refresh=true la salta y trae en vivo.
    api.get<{ id: string; subject: string }[]>('/whatsapp/groups', {
      timeout: 125_000,
      params:  refresh ? { refresh: 'true' } : undefined,
    }),
};

// ── WhatsApp Templates ────────────────────────────────────────────────────────
export const whatsappTemplatesApi = {
  list:      () =>
    api.get<import('@/types/api').WhatsappTemplateDto[]>('/whatsapp-templates'),
  variables: () =>
    api.get<import('@/types/api').TemplateVariable[]>('/whatsapp-templates/variables'),
  create:    (data: unknown) =>
    api.post<import('@/types/api').WhatsappTemplateDto>('/whatsapp-templates', data),
  update:    (id: string, data: unknown) =>
    api.put<import('@/types/api').WhatsappTemplateDto>(`/whatsapp-templates/${id}`, data),
  remove:    (id: string) =>
    api.delete(`/whatsapp-templates/${id}`),
  test:      (id: string, phone: string, companyId?: string | null) =>
    api.post<{ sent: boolean; phone: string }>(`/whatsapp-templates/${id}/test`, { phone, companyId }),
  missingDefaults:     () =>
    api.get<import('@/types/api').MissingDefaultTemplateDto[]>('/whatsapp-templates/missing-defaults'),
  seedMissingDefaults: () =>
    api.post<import('@/types/api').MissingDefaultTemplateDto[]>('/whatsapp-templates/missing-defaults/seed'),
  scheduled: (params?: {
    status?: string; templateId?: string; search?: string;
    dateFrom?: string; dateTo?: string; page?: number; pageSize?: number;
  }) =>
    api.get<import('@/types/api').ScheduledMessagesResponseDto>('/whatsapp-templates/scheduled', {
      params: cleanParams(params ?? {}),
    }),
  cancelScheduled: (id: string) =>
    api.post<{ id: string; status: string }>(`/whatsapp-templates/scheduled/${id}/cancel`),
  resendScheduled: (id: string) =>
    api.post<{ id: string; status: string; scheduledFor: string }>(`/whatsapp-templates/scheduled/${id}/resend`),
  previewScheduled: (id: string) =>
    api.get<import('@/types/api').ScheduledMessagePreviewDto>(`/whatsapp-templates/scheduled/${id}/preview`),
  addAttachment: (templateId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post<import('@/types/api').TemplateAttachmentDto>(`/whatsapp-templates/${templateId}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  addGeneratedAttachment: (templateId: string) =>
    api.post<import('@/types/api').TemplateAttachmentDto>(`/whatsapp-templates/${templateId}/attachments`, {
      generatorType: 'REPORTE_ESTADO_TRIBUTARIO',
    }),
  removeAttachment: (templateId: string, attachmentId: string) =>
    api.delete(`/whatsapp-templates/${templateId}/attachments/${attachmentId}`),
};

export const packagesApi = {
  list:             ()                              => api.get<import('@/types/api').ServicePackageDto[]>('/packages'),
  create:           (data: unknown)                 => api.post<import('@/types/api').ServicePackageDto>('/packages', data),
  update:           (id: string, data: unknown)     => api.put<import('@/types/api').ServicePackageDto>(`/packages/${id}`, data),
  remove:           (id: string)                    => api.delete(`/packages/${id}`),
  stats:            ()                              => api.get<import('@/types/api').PackageStatsDto>('/packages/stats'),
  listAssignments:  (pkgId: string)                 => api.get<import('@/types/api').CompanyPackageDto[]>(`/packages/${pkgId}/assignments`),
  allAssignments:   ()                              => api.get<import('@/types/api').CompanyPackageDto[]>('/packages/assignments/all'),
  assign:           (pkgId: string, data: unknown)  => api.post<import('@/types/api').CompanyPackageDto>(`/packages/${pkgId}/assign`, data),
  updateAssignment: (aId: string, data: unknown)    => api.put<import('@/types/api').CompanyPackageDto>(`/packages/assignments/${aId}`, data),
  removeAssignment: (aId: string)                   => api.delete(`/packages/assignments/${aId}`),
  createPayment:    (aId: string, data: unknown)    => api.post<import('@/types/api').PackagePaymentDto>(`/packages/assignments/${aId}/payments`, data),
  updatePayment:    (aId: string, pId: string, data: unknown) =>
    api.put<import('@/types/api').PackagePaymentDto>(`/packages/assignments/${aId}/payments/${pId}`, data),
  deletePayment:    (aId: string, pId: string)      => api.delete(`/packages/assignments/${aId}/payments/${pId}`),
  generatePeriods:  (aId: string, data: unknown)    => api.post(`/packages/assignments/${aId}/generate-periods`, data),
  uploadVoucher:    (aId: string, pId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<import('@/types/api').PackagePaymentDto>(
      `/packages/assignments/${aId}/payments/${pId}/vouchers`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },
  sendReminder:     (aId: string, pId: string)      => api.post<{ sent: boolean; to: string }>(`/packages/assignments/${aId}/payments/${pId}/send-reminder`),
};

// ── CRM Subscription ─────────────────────────────────────────────────────────

export const crmSubscriptionApi = {
  getCurrent: () => api.get<import('@/types/api').CrmCurrentPlanDto>('/crm-subscription'),
  sync:       () => api.post<import('@/types/api').CrmCurrentPlanDto>('/crm-subscription/sync'),
  getCatalog: () => api.get<import('@/types/api').CrmCatalogResponse>('/crm-subscription/catalog'),
  getHistory: () => api.get<import('@/types/api').CrmSubscriptionHistoryItem[]>('/crm-subscription/history'),
  getBilling: () => api.get<import('@/types/api').CrmBillingStatusDto>('/crm-subscription/billing'),
  createCheckoutSession: (data: { productId: number; billingPeriod: string }) =>
    api.post<import('@/types/api').CrmCheckoutSession>('/crm-subscription/checkout-session', data),
  getCheckoutSessionStatus: (id: string) =>
    api.get<import('@/types/api').CrmCheckoutSessionStatus>(`/crm-subscription/checkout-session/${id}`),
};

// ── Billing (cobranzas) ──────────────────────────────────────────────────────

export interface BillingPeriodsFilter {
  companyId?:        string;
  companyPackageId?: string;
  status?:           import('@/types/api').BillingPeriodStatus;
  isAdditional?:     boolean;
  from?:             string;
  to?:               string;
}

export interface BillingPaymentsFilter {
  companyId?:        string;
  companyPackageId?: string;
  method?:           import('@/types/api').PaymentMethod;
  status?:           import('@/types/api').BillingPaymentStatus;
  from?:             string;
  to?:               string;
}

export interface CreatePaymentPayload {
  companyPackageId: string;
  paymentDate:      string;
  amount:           number;
  discountAmount?:  number;
  discountReason?:  string | null;
  method:           import('@/types/api').PaymentMethod;
  reference?:       string | null;
  invoiceType?:     import('@/types/api').InvoiceType | null;
  invoiceSerie?:    string | null;
  invoiceNumber?:   string | null;
  notes?:           string | null;
  allocations:      { periodId: string; amount: number }[];
}

export interface CreatePeriodPayload {
  companyPackageId:     string;
  periodLabel:          string;
  periodStart:          string;
  periodEnd:            string;
  dueDate:              string;
  expectedAmount:       number;
  isAdditional:         boolean;
  additionalLabel?:     string | null;
  additionalCategoryId?: string | null;
  notes?:               string | null;
}

export interface UpdatePeriodPayload {
  periodLabel?:          string;
  periodStart?:          string;
  periodEnd?:            string;
  dueDate?:              string;
  expectedAmount?:       number;
  additionalLabel?:      string | null;
  additionalCategoryId?: string | null;
  notes?:                string | null;
  cancel?:               boolean;
}

export interface UpsertCategoryPayload {
  name:       string;
  color?:     string | null;
  sortOrder?: number;
  isActive?:  boolean;
}

function cleanParams<T extends Record<string, unknown>>(p: T): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(p)) {
    const v = p[k];
    if (v !== undefined && v !== null && v !== '') out[k] = v;
  }
  return out;
}

export const billingApi = {
  // Stats
  stats: () => api.get<import('@/types/api').BillingStatsDto>('/billing/stats'),

  // Periods
  listPeriods: (filter: BillingPeriodsFilter = {}) =>
    api.get<import('@/types/api').BillingPeriodDto[]>('/billing/periods', { params: cleanParams({ ...filter }) }),
  createPeriod: (data: CreatePeriodPayload) =>
    api.post<import('@/types/api').BillingPeriodDto>('/billing/periods', data),
  updatePeriod: (id: string, data: UpdatePeriodPayload) =>
    api.put<import('@/types/api').BillingPeriodDto>(`/billing/periods/${id}`, data),
  deletePeriod: (id: string) => api.delete(`/billing/periods/${id}`),
  generatePeriods: (data: { companyPackageId: string; periodsCount: number; startFrom?: string }) =>
    api.post<{ created: number }>('/billing/periods/generate', data),
  sendReminder: (
    periodId: string,
    customData?: { customTo?: string; customText?: string },
    attachments?: File[],
  ) => {
    if (attachments && attachments.length > 0) {
      const fd = new FormData();
      if (customData?.customTo)   fd.append('customTo',   customData.customTo);
      if (customData?.customText) fd.append('customText', customData.customText);
      attachments.forEach(f => { fd.append('attachments', f); });
      return api.post<{ sent: boolean; to: string; notificationId: string }>(
        `/billing/periods/${periodId}/reminder`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
    }
    return api.post<{ sent: boolean; to: string; notificationId: string }>(
      `/billing/periods/${periodId}/reminder`, customData,
    );
  },
  previewReminder: (periodId: string) =>
    api.get<{ text: string; to: string; sentThisMonth: number; templateName: string }>(
      `/billing/periods/${periodId}/reminder/preview`,
    ),

  previewThankYou: (paymentId: string) =>
    api.get<{ text: string; to: string; sentThisMonth: number; templateName: string }>(
      `/billing/payments/${paymentId}/thank-you/preview`,
    ),
  sendThankYou: (
    paymentId: string,
    customData?: { customTo?: string; customText?: string },
    attachments?: File[],
  ) => {
    if (attachments && attachments.length > 0) {
      const fd = new FormData();
      if (customData?.customTo)   fd.append('customTo',   customData.customTo);
      if (customData?.customText) fd.append('customText', customData.customText);
      attachments.forEach(f => { fd.append('attachments', f); });
      return api.post<{ sent: boolean; to: string; notificationId: string }>(
        `/billing/payments/${paymentId}/thank-you`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
    }
    return api.post<{ sent: boolean; to: string; notificationId: string }>(
      `/billing/payments/${paymentId}/thank-you`, customData,
    );
  },

  // Payments
  listPayments: (filter: BillingPaymentsFilter = {}) =>
    api.get<import('@/types/api').BillingPaymentDto[]>('/billing/payments', { params: cleanParams({ ...filter }) }),
  getPayment:   (id: string) =>
    api.get<import('@/types/api').BillingPaymentDto>(`/billing/payments/${id}`),
  createPayment: (data: CreatePaymentPayload) =>
    api.post<import('@/types/api').BillingPaymentDto>('/billing/payments', data),
  voidPayment:  (id: string, voidReason: string) =>
    api.post<import('@/types/api').BillingPaymentDto>(`/billing/payments/${id}/void`, { voidReason }),

  // Vouchers
  uploadVoucher: (paymentId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<import('@/types/api').BillingVoucherDto>(
      `/billing/payments/${paymentId}/vouchers`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },
  deleteVoucher: (id: string) => api.delete(`/billing/vouchers/${id}`),
  voucherUrl:    (id: string) =>
    api.get<{ url: string; fileName: string; mimeType: string }>(`/billing/vouchers/${id}/url`),

  // Categories
  listCategories:   () => api.get<import('@/types/api').AdditionalCategoryDto[]>('/billing/categories'),
  createCategory:   (data: UpsertCategoryPayload) =>
    api.post<import('@/types/api').AdditionalCategoryDto>('/billing/categories', data),
  updateCategory:   (id: string, data: UpsertCategoryPayload) =>
    api.put<import('@/types/api').AdditionalCategoryDto>(`/billing/categories/${id}`, data),
  deleteCategory:   (id: string) => api.delete(`/billing/categories/${id}`),

  // Reports
  aging: (cutoff?: string) =>
    api.get<import('@/types/api').AgingReportDto>('/billing/reports/aging', { params: cleanParams({ cutoff }) }),
  ventas: (from: string, to: string, groupBy: 'mes' | 'paquete' | 'metodo' | 'empresa' = 'mes') =>
    api.get<import('@/types/api').VentasGroupDto[]>('/billing/reports/ventas', { params: { from, to, groupBy } }),
  descuentos: (from: string, to: string, groupBy: 'mes' | 'empresa' | 'motivo' = 'mes') =>
    api.get<import('@/types/api').DescuentoGroupDto[]>('/billing/reports/descuentos', { params: { from, to, groupBy } }),
  extracto: (companyId: string, from: string, to: string) =>
    api.get<import('@/types/api').ExtractoEmpresaDto>(`/billing/reports/extracto/${companyId}`, { params: { from, to } }),
};

// ── Sync SUNAT ────────────────────────────────────────────────────────────────

export interface StartSyncPayload {
  periodFrom: string;        // YYYYMM
  periodTo:   string;        // YYYYMM
  modules:    import('@/types/api').SyncModuleKind[];
}

export interface BulkSyncPayload {
  /** Mes de trabajo (YYYYMM): el sync masivo siempre es de un solo período. */
  period:      string;
  companyIds?: string[];
}

export const syncApi = {
  start: (companyId: string, payload: StartSyncPayload) =>
    api.post<import('@/types/api').CompanySyncJobDto>(`/companies/${companyId}/sync`, payload),
  active: (companyId: string) =>
    api.get<import('@/types/api').CompanySyncJobDto | null>(`/companies/${companyId}/sync/active`),
  history: (companyId: string, limit = 20) =>
    api.get<import('@/types/api').CompanySyncJobDto[]>(`/companies/${companyId}/sync/history`, { params: { limit } }),
  get: (jobId: string) =>
    api.get<import('@/types/api').CompanySyncJobDto>(`/sync-jobs/${jobId}`),
  cancel: (jobId: string) =>
    api.post<import('@/types/api').CompanySyncJobDto>(`/sync-jobs/${jobId}/cancel`),
  startAll: (payload: BulkSyncPayload) =>
    api.post<import('@/types/api').BulkSyncLaunchResultDto>('/companies/sync-all', payload),
  batchStatus: (batchId: string) =>
    api.get<import('@/types/api').SyncBatchDto>(`/companies/sync-all/${batchId}`),
  workerHealth: () =>
    api.get<import('@/types/api').SyncWorkerHealthDto>('/sync/worker-health'),
};

export const reportsApi = {
  list:     (cId: string) => api.get(`/companies/${cId}/reports`),
  generate: (cId: string, data: { tipo: string; periodo: string }) => api.post(`/companies/${cId}/reports`, data),
  getUrl:   (cId: string, reportId: string) => api.get(`/companies/${cId}/reports/${reportId}/url`),
  getById:  (reportId: string) => api.get(`/reports/${reportId}`),
  // Legacy named endpoints
  analisisTributario: (cId: string, data?: unknown) => api.post(`/companies/${cId}/reports/analisis-tributario`, data ?? {}),
  multasDetalle:      (cId: string, data?: unknown) => api.post(`/companies/${cId}/reports/multas-detalle`,      data ?? {}),
  cronogramaPagos:    (cId: string, data?: unknown) => api.post(`/companies/${cId}/reports/cronograma-pagos`,    data ?? {}),
};

// ── Historial de Comunicaciones ──────────────────────────────────────────────

export interface CommLogFilters {
  companyId?:    string;
  channel?:      string;
  templateKey?:  string;
  isManual?:     boolean;
  sentByUserId?: string;
  status?:       string;
  dateFrom?:     string;
  dateTo?:       string;
  search?:       string;
  page?:         number;
  pageSize?:     number;
}

export interface CommLogItem {
  id:           string;
  channel:      string;
  status:       string;
  isManual:     boolean;
  recipient:    string | null;
  subject:      string | null;
  templateKey:  string | null;
  errorMessage: string | null;
  sentAt:       string | null;
  createdAt:    string;
  company?: {
    id: string;
    razonSocial: string;
    ruc: string;
    contactNombre?: string | null;
    contactTelefono?: string | null;
    contactEmail?: string | null;
    contactCargo?: string | null;
  } | null;
  sentBy:       { id: string; firstName: string; lastName: string; email: string } | null;
  _count:       { attachments: number };
}

export interface CommLogDetail extends CommLogItem {
  body:        string;
  metadata:    unknown;
  attachments: {
    id:         string;
    fileName:   string;
    mimeType:   string;
    sizeBytes:  number;
    uploadedAt: string;
    url:        string;
  }[];
}

export interface CommLogStats {
  total:          number;
  totalSent:      number;
  totalFailed:    number;
  totalLast7Days: number;
  byChannel:  { channel: string; count: number }[];
  byStatus:   { status: string;  count: number }[];
  byOrigin:   { isManual: boolean; count: number }[];
}

export const commLogApi = {
  list:   (filters: CommLogFilters = {}) =>
    api.get<{ data: CommLogItem[]; meta: { total: number; page: number; pageSize: number; totalPages: number } }>(
      '/comm-logs', { params: cleanParams(filters as Record<string, unknown>) },
    ),
  detail: (id: string) => api.get<CommLogDetail>(`/comm-logs/${id}`),
  stats:  (dateFrom?: string, dateTo?: string) =>
    api.get<CommLogStats>('/comm-logs/stats', { params: cleanParams({ dateFrom, dateTo }) }),
};
